import { Icons, Tooltip, useNotificationStore } from '@khal-os/ui';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef } from 'react';
import { useWindowStore } from '../../stores/window-store';
import type { WindowState } from '../../types/window';
import { AppIcon } from '../app-icon';
import { UserMenu } from './UserMenu';

interface PackEntry {
	appId: string;
	windows: WindowState[];
	active: boolean;
	title: string;
}

export function Taskbar({ onOpenPalette }: { onOpenPalette?: () => void }) {
	const windowsByWorkspace = useWindowStore((s) => s.windowsByWorkspace);
	const activeWorkspaceId = useWindowStore((s) => s.activeWorkspaceId);
	const focusWindow = useWindowStore((s) => s.focusWindow);
	const minimizeWindow = useWindowStore((s) => s.minimizeWindow);

	const unreadCount = useNotificationStore((s) => s.unreadCount);
	const toggleCenter = useNotificationStore((s) => s.toggleCenter);

	const windows = activeWorkspaceId ? windowsByWorkspace[activeWorkspaceId] || [] : [];

	const packs = useMemo<PackEntry[]>(() => {
		const byApp = new Map<string, PackEntry>();
		for (const win of windows) {
			const focused = win.focused && !win.minimized;
			const entry = byApp.get(win.appId);
			if (entry) {
				entry.windows.push(win);
				entry.active = entry.active || focused;
				if (focused) entry.title = win.title;
			} else {
				byApp.set(win.appId, { appId: win.appId, windows: [win], active: focused, title: win.title });
			}
		}
		return Array.from(byApp.values());
	}, [windows]);

	const timeRef = useRef<HTMLSpanElement>(null);
	const dateRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		const update = () => {
			const now = new Date();
			if (timeRef.current) {
				timeRef.current.textContent = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
			}
			if (dateRef.current) {
				dateRef.current.textContent = now.toLocaleDateString([], { month: 'short', day: 'numeric' });
			}
		};
		update();
		const id = setInterval(update, 10_000);
		return () => clearInterval(id);
	}, []);

	return (
		<motion.nav
			initial={{ opacity: 0, y: 16, scale: 0.96 }}
			animate={{ opacity: 1, y: 0, scale: 1 }}
			transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
			className="fixed inset-x-0 bottom-0 z-[9000] flex justify-center pb-2 pointer-events-none"
			aria-label="Taskbar"
		>
			<div
				className="k-taskbar-pill pointer-events-auto flex h-10 items-center gap-0.5 rounded-xl px-1.5"
				style={{
					background: 'var(--khal-taskbar-bg)',
					backdropFilter: 'blur(40px) saturate(1.8)',
					WebkitBackdropFilter: 'blur(40px) saturate(1.8)',
					border: '1px solid var(--khal-taskbar-border)',
					boxShadow: 'var(--khal-taskbar-shadow)',
				}}
			>
				{/* K-launcher */}
				<Tooltip text="App launcher (Cmd+K)" position="top" delay delayTime={400} desktopOnly>
					<motion.button
						className="k-btn-icon flex h-9 w-9 items-center justify-center rounded-full border-0 bg-transparent"
						style={{ color: 'var(--khal-text-secondary)' }}
						whileHover={{
							scale: 1.1,
							background: 'oklch(0.74 0.11 65 / 0.12)',
							boxShadow: '0 0 12px oklch(0.74 0.11 65 / 0.3)',
						}}
						whileTap={{ scale: 0.92 }}
						onClick={onOpenPalette}
						aria-label="Open app launcher"
					>
						<Icons.K size={18} />
					</motion.button>
				</Tooltip>

				<span
					className="k-taskbar-divider mx-0.5 h-4 w-px shrink-0"
					style={{ background: 'var(--khal-taskbar-divider)' }}
					aria-hidden="true"
				/>

				{/* Per-pack running-app indicators */}
				<div className="flex items-center gap-0.5" role="tablist" aria-label="Running applications">
					{packs.length === 0 ? (
						<span className="px-1 text-[11px] select-none" style={{ color: 'var(--khal-text-muted)' }}>
							No apps
						</span>
					) : (
						<AnimatePresence mode="popLayout">
							{packs.map((pack) => (
								<motion.div
									key={pack.appId}
									layout
									initial={{ opacity: 0, scale: 0.5, width: 0 }}
									animate={{ opacity: 1, scale: 1, width: 'auto' }}
									exit={{ opacity: 0, scale: 0.5, width: 0 }}
									transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
								>
									<Tooltip text={pack.title} position="top" delay delayTime={300} desktopOnly>
										<motion.button
											role="tab"
											aria-selected={pack.active}
											aria-label={`${pack.title}${pack.active ? ' (active)' : ' (open)'}`}
											className="k-taskbar-app relative flex h-9 w-9 items-center justify-center rounded-full transition-colors"
											style={{
												background: pack.active ? 'var(--khal-taskbar-active-bg)' : undefined,
											}}
											whileHover={{
												background: pack.active ? undefined : 'var(--khal-taskbar-hover-bg)',
											}}
											whileTap={{ scale: 0.95 }}
											onClick={() => {
												const target =
													pack.windows.find((w) => w.focused && !w.minimized) ?? pack.windows[0];
												if (target.focused && !target.minimized) {
													minimizeWindow(target.id);
												} else {
													focusWindow(target.id);
												}
											}}
										>
											<AppIcon appId={pack.appId} size={32} className="h-8 w-8 shrink-0" />

											{/* Per-pack running dot — Decision #11: `.k-badge-dot` does not exist,
											    so use a per-surface CSS hook for this indicator. */}
											<span
												className="k-taskbar-dot absolute -bottom-0.5 left-1/2 -translate-x-1/2 rounded-full transition-all"
												style={{
													width: pack.active ? 8 : 4,
													height: 3,
													background: pack.active
														? 'var(--khal-taskbar-active-indicator)'
														: 'var(--khal-text-muted)',
													opacity: pack.active ? 1 : 0.6,
												}}
												aria-hidden="true"
											/>
										</motion.button>
									</Tooltip>
								</motion.div>
							))}
						</AnimatePresence>
					)}
				</div>

				<span
					className="k-taskbar-divider mx-0.5 h-4 w-px shrink-0"
					style={{ background: 'var(--khal-taskbar-divider)' }}
					aria-hidden="true"
				/>

				{/* Clock + date */}
				<Tooltip text="Current time" position="top" delay delayTime={400} desktopOnly>
					<div className="hidden items-center gap-1.5 px-1.5 select-none sm:flex">
						<span
							ref={timeRef}
							className="text-[11px] font-medium tabular-nums"
							style={{ color: 'var(--khal-text-secondary)' }}
						/>
						<span ref={dateRef} className="text-[11px] tabular-nums" style={{ color: 'var(--khal-text-muted)' }} />
					</div>
				</Tooltip>

				{/* Notification bell */}
				<Tooltip text="Notifications" position="top" delay delayTime={400} desktopOnly>
					<motion.button
						className="k-btn-icon relative flex h-7 w-7 items-center justify-center rounded-full border-0 bg-transparent"
						style={{ color: 'var(--khal-text-secondary)' }}
						whileHover={{
							scale: 1.08,
							background: 'var(--khal-taskbar-hover-bg)',
						}}
						whileTap={{ scale: 0.92 }}
						onClick={toggleCenter}
						aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
					>
						<Icons.Bell size={14} />
						{unreadCount > 0 && (
							<motion.span
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								className="k-taskbar-count absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full px-0.5 text-[8px] font-bold"
								style={{
									background: 'var(--khal-accent-primary)',
									color: 'var(--khal-text-inverse)',
								}}
								aria-hidden="true"
							>
								{unreadCount > 99 ? '99+' : unreadCount}
							</motion.span>
						)}
					</motion.button>
				</Tooltip>

				{/* User avatar */}
				<UserMenu />
			</div>
		</motion.nav>
	);
}
